/**
 * Execute JRiver Media Center keyboard actions in Maestro.
 * Matching rules live in jriverShortcutMap.ts.
 */

import { getClientTransport } from '../handlers/ClientTransportHandler';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { useMaestroStore } from '../store/maestroStore';
import { TransportCommand, stop as transportStop } from '../transport/TransportCommand';
import { QueueController } from '../transport/QueueController';
import { ZoneController } from '../transport/ZoneController';
import { getDisplayVolume } from '../transport/volumeHydration';
import { isConnectorBitPerfectBlocked, bitPerfectBlockedMessage } from '../utils/connectorCapability';
import { showImperativeToast } from '../providers/UIProvider';
import {
  isTypingTarget,
  matchJriverShortcut,
  JRIVER_JUMP_STEP_SEC,
  JRIVER_SEEK_STEP_SEC,
  type JriverShortcutAction,
} from './jriverShortcutMap';

export { JRIVER_JUMP_STEP_SEC, JRIVER_SEEK_STEP_SEC, isTypingTarget, isJriverCtrl, matchJriverShortcut } from './jriverShortcutMap';
export type { JriverShortcutAction } from './jriverShortcutMap';

const PLAY_PAUSE_DEBOUNCE_MS = 350;
let _lastPlayPauseAt = 0;

function blockBitPerfectPlay(): boolean {
  const active = useMaestroStore.getState().discovery?.activeZone;
  if (active?.type === 'local-native' && isConnectorBitPerfectBlocked()) {
    showImperativeToast(bitPerfectBlockedMessage() || 'Bit-perfect playback requires a newer Connector — please update.', 5000);
    return true;
  }
  return false;
}

function togglePlayPause(): void {
  const now = Date.now();
  if (now - _lastPlayPauseAt < PLAY_PAUSE_DEBOUNCE_MS) return;
  _lastPlayPauseAt = now;

  if (blockBitPerfectPlay()) return;

  getSyncHandler().claimControl();
  const ps = useMaestroStore.getState().playbackState;
  if (ps === 'playing' || ps === 'loading') {
    void TransportCommand.pause(true);
  } else {
    void TransportCommand.play();
  }
}

function adjustVolume(delta: number): void {
  const store = useMaestroStore.getState();
  const rendererId = store.activeConnectorRenderer?.id;
  const current = getDisplayVolume(store.volume, rendererId);
  const next = Math.max(0, Math.min(100, current + delta));
  void TransportCommand.volume(next);
}

function seekRelative(deltaSec: number): void {
  const store = useMaestroStore.getState();
  const pos = store.position ?? 0;
  const dur = store.duration ?? 0;
  const max = dur > 0 ? dur : Number.POSITIVE_INFINITY;
  const next = Math.max(0, Math.min(max, pos + deltaSec));
  void TransportCommand.seek(next);
}

async function toggleMute(): Promise<void> {
  const store = useMaestroStore.getState();
  const transport = getClientTransport();
  if (store.muted) await transport.unmute();
  else await transport.mute();
}

function cycleOutputZone(): void {
  const store = useMaestroStore.getState();
  const zones = store.discovery?.discoveredZones ?? [];
  if (zones.length <= 1) return;

  const currentId = store.discovery?.activeZone?.id;
  const idx = zones.findIndex((z) => z.id === currentId);
  const next = zones[(idx + 1 + zones.length) % zones.length];
  void ZoneController.selectZone(next);
}

export type JriverShortcutHandlers = {
  onFind?: () => void;
};

export function executeJriverShortcut(action: JriverShortcutAction, handlers: JriverShortcutHandlers = {}): void {
  switch (action) {
    case 'playPause':
      togglePlayPause();
      break;
    case 'stop':
      getSyncHandler().claimControl();
      void transportStop();
      break;
    case 'previous':
      getSyncHandler().claimControl();
      void QueueController.previous();
      break;
    case 'next':
      getSyncHandler().claimControl();
      void QueueController.next();
      break;
    case 'muteToggle':
      void toggleMute();
      break;
    case 'volumeUp':
      adjustVolume(1);
      break;
    case 'volumeDown':
      adjustVolume(-1);
      break;
    case 'seekBack':
      seekRelative(-JRIVER_SEEK_STEP_SEC);
      break;
    case 'seekForward':
      seekRelative(JRIVER_SEEK_STEP_SEC);
      break;
    case 'jumpBack':
      seekRelative(-JRIVER_JUMP_STEP_SEC);
      break;
    case 'jumpForward':
      seekRelative(JRIVER_JUMP_STEP_SEC);
      break;
    case 'find':
      handlers.onFind?.();
      break;
    case 'cycleZone':
      getSyncHandler().claimControl();
      cycleOutputZone();
      break;
    default:
      break;
  }
}

export function handleJriverKeyDown(
  e: KeyboardEvent,
  handlers: JriverShortcutHandlers = {},
): boolean {
  const action = matchJriverShortcut(e);
  if (!action) return false;
  if (isTypingTarget(e.target)) return false;

  e.preventDefault();
  e.stopPropagation();
  executeJriverShortcut(action, handlers);
  return true;
}
